import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from 'flowbite-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredStatus?: 'pending_approval' | 'active' | 'inactive';
  requiredRole?: 'superadmin' | 'admin' | 'user';
  redirectTo?: string;
  allowUnauthenticated?: boolean;
}

export default function ProtectedRoute({
  children,
  requiredStatus,
  requiredRole,
  redirectTo,
  allowUnauthenticated = false,
}: ProtectedRouteProps) {
  const { user, userProfile, loading } = useAuth();

  // Mostrar spinner mientras carga
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Spinner size="xl" className="text-blue-600" />
      </div>
    );
  }

  // Si no requiere autenticación, permitir acceso
  if (allowUnauthenticated) {
    return <>{children}</>;
  }

  // Si no hay usuario, redirigir al login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si no hay perfil de usuario, redirigir a waiting-approval
  if (!userProfile) {
    return <Navigate to="/waiting-approval" replace />;
  }

  // Verificar rol requerido
  if (requiredRole && userProfile.role !== requiredRole) {
    // Si requiere superadmin pero no lo es, redirigir al dashboard
    if (requiredRole === 'superadmin') {
      return <Navigate to="/dashboard" replace />;
    }
    // Para otros roles, redirigir a la ruta especificada o dashboard
    return <Navigate to={redirectTo || '/dashboard'} replace />;
  }

  // Verificar estado requerido
  if (requiredStatus) {
    if (userProfile.status !== requiredStatus) {
      // Si requiere 'active' pero está en 'pending_approval', redirigir a waiting-approval
      if (requiredStatus === 'active' && userProfile.status === 'pending_approval') {
        return <Navigate to="/waiting-approval" replace />;
      }
      // Si requiere 'pending_approval' pero está activo, redirigir al dashboard
      if (requiredStatus === 'pending_approval' && userProfile.status === 'active') {
        return <Navigate to="/dashboard" replace />;
      }
      // Si está inactivo, redirigir a waiting-approval o login
      if (userProfile.status === 'inactive') {
        return <Navigate to={redirectTo || '/waiting-approval'} replace />;
      }
      // Para otros casos, redirigir a la ruta especificada
      return <Navigate to={redirectTo || '/dashboard'} replace />;
    }
  } else {
    // Si no se especifica estado requerido, pero el usuario está inactivo, redirigir
    if (userProfile.status === 'inactive') {
      return <Navigate to="/waiting-approval" replace />;
    }
  }

  // Si pasa todas las validaciones, renderizar el componente
  return <>{children}</>;
}
